import React, { useEffect, useId, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import DatePicker from 'react-datepicker'
import ru from 'date-fns/locale/ru'
import enGB from 'date-fns/locale/en-GB'
import format from 'date-fns/format'
import { tr } from 'i18n'
import 'react-datepicker/dist/react-datepicker.css'
import styles from './LocalizedDateInput.module.scss'

const months = ['Январь', 'Февраль', 'Март', 'Апрель', 'Май', 'Июнь', 'Июль', 'Август', 'Сентябрь', 'Октябрь', 'Ноябрь', 'Декабрь']
const weekdays = ['Воскресенье', 'Понедельник', 'Вторник', 'Среда', 'Четверг', 'Пятница', 'Суббота']
const ky = {
  ...ru,
  code: 'ky',
  formatLong: {
    ...ru.formatLong,
    date: ({ width = 'full' } = {}) => ({ full: 'EEEE, d MMMM yyyy', long: 'd MMMM yyyy', medium: 'd MMM yyyy', short: 'dd.MM.yyyy' })[width],
  },
  localize: {
    ...ru.localize,
    ordinalNumber: number => String(number),
    month: (index, { width = 'wide' } = {}) => {
      const value = tr(months[index]).toLocaleLowerCase('ky-KG')
      return width === 'narrow' ? value.slice(0, 1) : width === 'abbreviated' ? value.slice(0, 3) : value
    },
    day: (index, { width = 'wide' } = {}) => {
      const value = tr(weekdays[index])
      return width === 'narrow' ? value.slice(0, 1) : width === 'short' ? value.slice(0, 2) : width === 'abbreviated' ? value.slice(0, 3) : value
    },
  },
}

export function parseIsoDate(value) {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(value || '')) return null
  const [year, month, day] = value.split('-').map(Number)
  const date = new Date(year, month - 1, day)
  return date.getFullYear() === year && date.getMonth() === month - 1 && date.getDate() === day ? date : null
}
export function toIsoDate(date) {
  return date ? `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}` : ''
}

export default function LocalizedDateInput({
  value, defaultValue = '', onChange, min, max, required, name, id, className = '', style,
  disabled, readOnly, 'aria-label': accessibleLabel, 'aria-describedby': describedBy,
  'aria-labelledby': labelledBy, 'aria-invalid': invalid, placeholder, onBlur, onFocus, ...inputProps
}) {
  const { i18n } = useTranslation()
  const generatedId = useId()
  const inputId = id || `date-${generatedId}`
  const hiddenInput = useRef(null)
  const picker = useRef(null)
  const [uncontrolled, setUncontrolled] = useState(defaultValue)
  const [validationError, setValidationError] = useState('')
  const isoValue = value === undefined ? uncontrolled : value || ''
  const language = i18n.language
  const locale = language === 'en' ? enGB : language === 'ru' ? ru : ky
  const dateFormat = language === 'en' ? 'dd/MM/yyyy' : 'dd.MM.yyyy'
  const selected = parseIsoDate(isoValue)
  const outsideRange = isoValue && ((min && isoValue < min) || (max && isoValue > max))

  useEffect(() => {
    const message = validationError || (outsideRange ? 'Выберите дату в допустимом диапазоне.' : '') || (required && !isoValue ? 'Выбрать дату' : '')
    picker.current?.input?.setCustomValidity(message ? tr(message) : '')
  }, [language, validationError, required, isoValue, outsideRange])

  function handleChange(date, originalEvent) {
    const nextValue = toIsoDate(date)
    const error = nextValue && ((min && nextValue < min) || (max && nextValue > max)) ? 'Выберите дату в допустимом диапазоне.' : ''
    if (value === undefined) setUncontrolled(nextValue)
    if (hiddenInput.current) hiddenInput.current.value = nextValue
    setValidationError(error)
    const event = {
      type: 'change', target: hiddenInput.current, currentTarget: hiddenInput.current,
      nativeEvent: originalEvent?.nativeEvent || originalEvent,
      preventDefault: () => originalEvent?.preventDefault?.(),
      stopPropagation: () => originalEvent?.stopPropagation?.(),
      persist: () => {},
    }
    onChange?.(event)
  }

  function validateRaw(event) {
    if (typeof event?.target?.value !== 'string') return
    const raw = event.target.value
    if (!raw) { setValidationError(''); event.target.setCustomValidity(''); return }
    const parts = /^(\d{2})[./](\d{2})[./](\d{4})$/.exec(raw)
    const parsed = parts && parseIsoDate(`${parts[3]}-${parts[2]}-${parts[1]}`)
    const nextValue = toIsoDate(parsed)
    const error = !parsed ? 'Введите корректную дату.' : (min && nextValue < min) || (max && nextValue > max) ? 'Выберите дату в допустимом диапазоне.' : ''
    setValidationError(error)
    event.target.setCustomValidity(error ? tr(error) : '')
    if (parsed && error) {
      // Native date inputs also deliver valid ISO dates outside min/max so
      // existing range controls can disable their Apply action.
      handleChange(parsed, event)
      event.preventDefault()
    }
  }

  return (
    <span className={styles.root}>
      <DatePicker
        {...inputProps}
        key={language}
        ref={picker}
        id={inputId}
        selected={selected}
        onChange={handleChange}
        onChangeRaw={validateRaw}
        onBlur={onBlur}
        onFocus={onFocus}
        minDate={parseIsoDate(min) || undefined}
        maxDate={parseIsoDate(max) || undefined}
        required={required}
        disabled={disabled}
        readOnly={readOnly}
        locale={locale}
        dateFormat={dateFormat}
        dateFormatCalendar="LLLL yyyy"
        calendarStartDay={1}
        strictParsing
        autoComplete="off"
        placeholderText={placeholder || tr('дд.мм.гггг')}
        className={`${styles.input} ${className}`}
        wrapperClassName={styles.wrapper}
        calendarClassName={styles.calendar}
        popperClassName={styles.popper}
        popperPlacement="bottom-start"
        previousMonthAriaLabel={tr('Предыдущий месяц')}
        previousMonthButtonLabel={tr('Предыдущий месяц')}
        nextMonthAriaLabel={tr('Следующий месяц')}
        nextMonthButtonLabel={tr('Следующий месяц')}
        chooseDayAriaLabelPrefix={tr('Выбрать дату')}
        disabledDayAriaLabelPrefix={tr('Дата недоступна')}
        monthAriaLabelPrefix={tr('Месяц')}
        ariaDescribedBy={describedBy}
        ariaLabelledBy={labelledBy}
        ariaInvalid={invalid || Boolean(validationError || outsideRange)}
        ariaRequired={required}
        customInput={<input type="text" style={style} min={min} max={max} aria-label={accessibleLabel} inputMode="numeric" />}
      />
      <input ref={hiddenInput} type="hidden" id={`${inputId}-value`} name={name} value={isoValue} min={min} max={max} disabled={disabled} readOnly />
      <span className={styles.live} aria-live="polite">
        {selected ? tr('Выбрана дата: {{date}}', { date: format(selected, 'PPPP', { locale }) }) : ''}
      </span>
    </span>
  )
}
